import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, ExternalLink, Send } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { fadeUpVariant, staggerContainer } from '../lib/animations';
import SEO from '../components/common/SEO';
import { toast } from 'sonner';

const Contact = () => {
  const { getSetting } = useSiteSettings();
  const { language, t, getLocalizedField } = useLanguage();
  const [stores, setStores] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  // Get contact info from settings
  const companyName = getSetting('company_name', 'VAV Wear');
  const email = getSetting('email', 'info@vavwear.com');
  const phone = getSetting('phone', '+90 850 123 45 67');
  const whatsapp = getSetting('whatsapp', '+905551234567');

  // Fetch stores from API
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`);
        if (response.ok) {
          const data = await response.json();
          setStores(data.stores || []);
        }
      } catch (err) {
        // Use default stores if API fails
        setStores([
          {
            id: 1,
            name: 'VAV Wear Showroom',
            name_en: 'VAV Wear Showroom',
            address: 'Maslak Mah. Ahi Evran Cad. No:6 Maslak 42, Sarıyer/İstanbul',
            address_en: 'Maslak Mah. Ahi Evran Str. No:6 Maslak 42, Sarıyer/Istanbul',
            phone: '+90 212 123 45 67',
            email: 'showroom@vavwear.com',
            hours: 'Pazartesi - Cumartesi: 10:00 - 19:00',
            hours_en: 'Monday - Saturday: 10:00 AM - 7:00 PM',
            mapUrl: 'https://maps.google.com/?q=41.1082,29.0202'
          }
        ]);
      }
    };
    fetchStores();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success(t('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.', 'Your message has been sent successfully! We will get back to you shortly.'));
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setSending(false);
  };

  return (
    <motion.main 
      className="min-h-screen bg-vav-bg pt-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <SEO 
        title={t('İletişim', 'Contact')}
        description={t(
          `${companyName} ile iletişime geçin. Mağaza adresleri, telefon ve e-posta bilgileri.`,
          `Get in touch with ${companyName}. Store addresses, phone and email information.`
        )}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-vav-gold/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-vav-gold font-mono text-sm tracking-widest uppercase">
              {t('Bize Ulaşın', 'Get in Touch')}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-vav-fg mt-4">
              {t('İLETİŞİM', 'CONTACT')}
            </h1>
            <p className="text-vav-fg-muted mt-4 max-w-2xl mx-auto">
              {t(
                'Sorularınız için bizimle iletişime geçebilir veya showroom\'larımızı ziyaret edebilirsiniz.',
                'You can contact us for your questions or visit our showrooms.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-12 border-y border-vav-border bg-vav-bg-paper/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.a
              href={`tel:${phone.replace(/\s/g, '')}`}
              variants={fadeUpVariant}
              className="flex items-center gap-4 p-6 bg-vav-bg rounded-xl border border-vav-border hover:border-vav-gold/50 transition-colors group"
            >
              <div className="w-12 h-12 bg-vav-gold/10 rounded-full flex items-center justify-center group-hover:bg-vav-gold/20 transition-colors">
                <Phone className="w-5 h-5 text-vav-gold" />
              </div>
              <div>
                <p className="text-vav-fg-muted text-sm">{t('Telefon', 'Phone')}</p>
                <p className="text-vav-fg font-medium">{phone}</p>
              </div>
            </motion.a>

            <motion.a
              href={`mailto:${email}`}
              variants={fadeUpVariant}
              className="flex items-center gap-4 p-6 bg-vav-bg rounded-xl border border-vav-border hover:border-vav-gold/50 transition-colors group"
            >
              <div className="w-12 h-12 bg-vav-gold/10 rounded-full flex items-center justify-center group-hover:bg-vav-gold/20 transition-colors">
                <Mail className="w-5 h-5 text-vav-gold" />
              </div>
              <div>
                <p className="text-vav-fg-muted text-sm">{t('E-posta', 'Email')}</p>
                <p className="text-vav-fg font-medium">{email}</p>
              </div>
            </motion.a>

            <motion.a
              href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeUpVariant}
              className="flex items-center gap-4 p-6 bg-vav-bg rounded-xl border border-vav-border hover:border-green-500/50 transition-colors group"
            >
              <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <div>
                <p className="text-vav-fg-muted text-sm">WhatsApp</p>
                <p className="text-vav-fg font-medium">{t('Hemen Yazın', 'Message Now')}</p>
              </div>
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Stores Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-vav-gold font-mono text-sm tracking-widest uppercase">
              {t('Lokasyonlar', 'Locations')}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl text-vav-fg mt-2">
              {t('MAĞAZALARIMIZ', 'OUR STORES')}
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stores.map((store, index) => (
              <motion.div
                key={store.id || store.store_id || index}
                variants={fadeUpVariant}
                className="bg-vav-bg-paper rounded-xl border border-vav-border overflow-hidden hover:border-vav-gold/30 transition-colors"
              >
                <div className="p-6">
                  <h3 className="font-heading text-xl text-vav-fg mb-4">
                    {getLocalizedField(store, 'name')}
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-vav-gold mt-1 flex-shrink-0" />
                      <span className="text-vav-fg-muted">{getLocalizedField(store, 'address')}</span>
                    </div>
                    
                    {store.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-vav-gold flex-shrink-0" />
                        <a href={`tel:${store.phone.replace(/\s/g, '')}`} className="text-vav-fg-muted hover:text-vav-gold">
                          {store.phone}
                        </a>
                      </div>
                    )}
                    
                    {store.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-vav-gold flex-shrink-0" />
                        <a href={`mailto:${store.email}`} className="text-vav-fg-muted hover:text-vav-gold">
                          {store.email}
                        </a>
                      </div>
                    )}
                    
                    {(store.hours || store.hours_en) && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-vav-gold flex-shrink-0" />
                        <span className="text-vav-fg-muted">{getLocalizedField(store, 'hours')}</span>
                      </div>
                    )}
                  </div>
                  
                  {(store.mapUrl || store.map_url) && (
                    <a
                      href={store.mapUrl || store.map_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-vav-gold hover:text-vav-accent-cyan text-sm transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('Haritada Göster', 'Show on Map')}
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-vav-bg-paper/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-vav-gold font-mono text-sm tracking-widest uppercase">
              {t('Mesaj Gönderin', 'Send a Message')}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl text-vav-fg mt-2">
              {t('İLETİŞİM FORMU', 'CONTACT FORM')}
            </h2>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-vav-bg rounded-xl border border-vav-border p-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-vav-fg-muted text-sm mb-2">
                  {t('Ad Soyad', 'Full Name')} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-vav-bg-subtle border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-vav-fg-muted text-sm mb-2">
                  {t('E-posta', 'Email')} *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-vav-bg-subtle border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-vav-fg-muted text-sm mb-2">
                  {t('Telefon', 'Phone')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-vav-bg-subtle border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-vav-fg-muted text-sm mb-2">
                  {t('Konu', 'Subject')} *
                </label>
                <select
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full px-4 py-3 bg-vav-bg-subtle border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none transition-colors"
                >
                  <option value="">{t('Seçiniz...', 'Select...')}</option>
                  <option value="genel">{t('Genel Bilgi', 'General Info')}</option>
                  <option value="urun">{t('Ürün Bilgisi', 'Product Info')}</option>
                  <option value="siparis">{t('Sipariş Takibi', 'Order Tracking')}</option>
                  <option value="kurumsal">{t('Kurumsal İşbirliği', 'Corporate Partnership')}</option>
                  <option value="bayi">{t('Bayilik Başvurusu', 'Dealership Application')}</option>
                  <option value="diger">{t('Diğer', 'Other')}</option>
                </select>
              </div>
            </div>
            
            <div className="mt-6">
              <label className="block text-vav-fg-muted text-sm mb-2">
                {t('Mesajınız', 'Your Message')} *
              </label>
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 bg-vav-bg-subtle border border-vav-border rounded-lg text-vav-fg focus:border-vav-gold focus:outline-none transition-colors resize-none"
              />
            </div>
            
            <button
              type="submit"
              disabled={sending}
              className="mt-6 w-full tactical-btn flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {t('Gönder', 'Send')}
                </>
              )}
            </button>
          </motion.form>
        </div>
      </section>
    </motion.main>
  );
};

export default Contact;
